import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { FilterOptions } from '@shared/interfaces/filterOptions.interface';
import { ApiQuoterService } from '../api/api-quoter.service';
import { UserRole } from '@data/enums/user-role.enum';
import { ALLOWED_FINANCING_TERMS, FINANCING_TERM_OPTIONS } from '@data/constants/financing-terms.constant';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class QuoterService {
  entity: FormGroup
  role: string = UserRole.BROKER
  formMethod = 'UNSAVE'
  quote!: any
  customers: { label: string; value: string; }[] = []
  actives: { label: string; value: string; amount: number }[] = []
  sectors: { label: string; value: string; }[] = []
  // Plazos permitidos: única y exclusivamente 36/48/60 meses.
  // Source of truth en @data/constants/financing-terms.constant.
  termInMonths: { label: string; value: number; disabled?: boolean }[] = [...FINANCING_TERM_OPTIONS]
  termsArray: FormArray = this.fb.array([]); // {term: number, residuary: number}
  accessories!: FormArray

  private quoteAmountsSubject = new BehaviorSubject<any[]>([{
    term: 0,
    irr: 0,
    advance: 0,
    openingCommission: 0,
    advancedRents: 0,
    ratificationExpenses: 0,
    accessories: [],
    advanceSubtotal: 0,
    advanceIva: 0,
    advanceTotal: 0,
    monthlyCapital: 0,
    monthlyInterest: 0,
    monthlyAccesories: [],
    monthlySubtotal: 0,
    monthlyIva: 0,
    monthlyTotal: 0,
    residuary: 0,
    residuaryIva: 0,
    residuaryTotal: 0,
  }]);
  public quoteAmounts$ = this.quoteAmountsSubject.asObservable();

  constructor(
    private fb: FormBuilder,
    private apiQuoteService: ApiQuoterService
  ) {
    this.entity = this.fb.group({});
    this.termsArray = this.fb.array([]);
    this.entity.addControl('termsArray', this.termsArray);
    this.newEntity()
  }

  updateQuoteAmounts(newQuoteAmounts: any[]) {
    this.quoteAmountsSubject.next(newQuoteAmounts);
  }

  newEntity() {
    switch (this.formMethod) {
      case 'UNSAVE':
        this.entity = this.fb.group({
          case: ['CREATE'],
          provitionalClient: ['', [Validators.required]],
          provitionalAsset: ['', [Validators.required]],
          provitionalValue: [0, [Validators.required, Validators.min(1)]],
          leaseType: ['Puro'],
          terms: [[], [Validators.required]],
          paymentStart: ['', [Validators.required]],
          advance: [5, [Validators.required]],
          interestRate: [24],
          provisionPe: [12],
          openingCommission: [3, [Validators.required]],
          advancedRents: [0, [Validators.required]],
          ratificationExpenses: [3500],
          accessories: this.fb.array([]),
        });
        break;
      case 'AMOUNTS':
        this.entity = this.fb.group({
          case: ['AMOUNTS'],
          provitionalClient: ['', [Validators.required]],
          provitionalAsset: ['', [Validators.required]],
          leaseType: ['Puro'],
          terms: [[], [Validators.required]],
          paymentStart: ['', [Validators.required]],
          advance: [5, [Validators.required]],
          residuary: [5, [Validators.required]],
          interestRate: [24],
          provisionPe: [12, [Validators.required]],
          openingCommission: [3, [Validators.required]],
          advancedRents: [0, [Validators.required]],
          ratificationExpenses: [3500],
          accessories: this.fb.array([]),
        })
        break;
    }
  }

  get termsArrayControls() {
    // Devuelve los controles del FormArray como un array para iterarlos en la plantilla
    return this.termsArray.controls as FormGroup[];
  }

  setFormMethod(method: string) {
    this.formMethod = method;
  }

  getCustomerName(id: string) {
    return this.customers.find(c => c.value === id)?.label
  }

  getActiveName(id: string) {
    return this.actives.find(c => c.value === id)?.label
  }

  getSectorName(id: string) {
    return this.sectors.find(c => c.value === id)?.label
  }

  /**
   * Maneja cambios en la selección de plazos preservando los valores de residuary existentes
   * @param selectedTerms Array de plazos seleccionados (en meses)
   */
  onTermsChange(selectedTerms: number[]): void {
    // Preservar los valores de residuary existentes antes de limpiar
    const existingResiduaryValues: Map<number, number | null> = new Map();
    this.termsArray.controls.forEach((control) => {
      const term = control.get('term')?.value;
      const residuary = control.get('residuary')?.value;
      if (term !== undefined) {
        existingResiduaryValues.set(term, residuary);
      }
    });

    // Limpiar y recrear el array preservando valores existentes
    this.termsArray.clear();
    selectedTerms.forEach((term) => {
      const existingResiduary = existingResiduaryValues.get(term);
      this.addTermWithResiduary(term, existingResiduary ?? null);
    });

    // Actualiza el control 'terms' en el formulario principal para reflejar los términos seleccionados
    this.entity.get('terms')?.setValue(selectedTerms);
  }

  /**
   * Agrega un término con un valor de residuary específico
   * @param term Plazo en meses
   * @param residuary Valor del residuary (porcentaje) o null para nuevo
   */
  addTermWithResiduary(term: number, residuary: number | null): void {
    const minResiduary = this.role === UserRole.BROKER ? 5 : 0;
    const maxResiduary = this.role === UserRole.BROKER ? 30 : 100;
    
    this.termsArray.push(
      this.fb.group({
        term: [term, Validators.required],
        residuary: [residuary, [Validators.required, Validators.min(minResiduary), Validators.max(maxResiduary)]],
      })
    );
  }

  /**
   * Agrega un nuevo término con residuary vacío (para nuevos plazos)
   * @param term Plazo en meses
   */
  addTerm(term: number): void {
    this.addTermWithResiduary(term, null);
  }

  getTermInMonths(value: number | number[]): string {
    if (Array.isArray(value)) {
      return value.map(val => this.termInMonths.find(term => term.value === val)?.label ?? `${val}`).join(', ');
    } else {
      // Fallback a `${value}` para no dejar label vacío en cotizaciones
      // históricas con plazos ya retirados del catálogo.
      return this.termInMonths.find(term => term.value === value)?.label ?? `${value}`;
    }
  }

  /**
   * Candado de plazos permitidos. Un plazo es válido para submit si es una
   * opción vigente del catálogo (36/48/60) o un plazo legacy inyectado como
   * opción deshabilitada en modo edición. Cotizador público = create-only,
   * así que aquí solo pasa 36/48/60.
   */
  isTermAllowed(term: number): boolean {
    return ALLOWED_FINANCING_TERMS.includes(term) || this.termInMonths.some(o => o.value === term);
  }
}
